import React from "react";
import useSWR from "swr";
import { Link } from "react-router-dom";
import {
  Phone, MessageCircle, Wallet, Hammer, FileText, MessageSquareWarning,
  ChevronRight, AlertCircle, Sparkles, MapPin, Calendar, KeyRound,
} from "lucide-react";
import { api, fmtINR, fmtINRcompact, fmtDate, relativeTime } from "@/lib/api";
import { Skeleton } from "@/components/Atoms";

const fetcher = (url) => api.get(url).then((r) => r.data);

// Stock imagery for project hero — random but stable per project
const PROJECT_HERO = (id) => {
  const pool = [
    "https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?auto=format&fit=crop&w=1600&q=80",
    "https://images.unsplash.com/photo-1582407947304-fd86f028f716?auto=format&fit=crop&w=1600&q=80",
    "https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?auto=format&fit=crop&w=1600&q=80",
    "https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?auto=format&fit=crop&w=1600&q=80",
  ];
  return pool[(id || 0) % pool.length];
};

export default function BuyerHome() {
  const { data: flat } = useSWR("/me/flat", fetcher);
  const { data: fin }  = useSWR("/me/financials", fetcher);
  const { data: activity } = useSWR("/me/activity", fetcher);
  const { data: progress } = useSWR("/me/progress", fetcher);

  const possessionDays = (() => {
    const d = flat?.project?.completion_date;
    if (!d) return null;
    const diff = Math.ceil((new Date(d).getTime() - Date.now()) / (1000 * 60 * 60 * 24));
    return diff;
  })();

  const outstanding = fin?.summary?.outstanding ?? 0;
  const completionPct = progress?.completion_pct ?? 0;
  const heroImg = PROJECT_HERO(flat?.project?.project_id);

  return (
    <div className="space-y-5">
      {/* Hero card — image-rich */}
      <div className="rc-image-card rc-shine rc-rise" data-testid="hero-card" style={{ minHeight: 240 }}>
        {flat ? (
          <>
            <div className="rc-image-card-bg rc-kenburns" style={{ backgroundImage: `url(${heroImg})` }}/>
            <div className="rc-image-card-overlay"/>
            <div className="rc-image-card-content flex flex-col h-full">
              <div className="rc-tag" style={{ alignSelf: "flex-start" }} data-no-shadow><Sparkles size={12}/> Your home</div>
              <div className="mt-2">
                <div className="rc-title-1 text-white" style={{ fontSize: "clamp(20px, 5.5vw, 30px)", lineHeight: 1.15, textShadow: "0 2px 6px rgba(0,0,0,0.85), 0 0 12px rgba(0,0,0,0.5)" }}>{flat.project?.project_name}</div>
                <div className="inline-flex items-center gap-1.5 flex-wrap mt-1.5 px-2.5 py-1 rounded-md w-fit" data-no-shadow style={{ background: "rgba(0,0,0,0.55)", backdropFilter: "blur(6px)", WebkitBackdropFilter: "blur(6px)", fontSize: 13, color: "#fff" }}>
                  <MapPin size={12} className="text-[#F9A61C]"/>
                  <span>{flat.project?.location}</span>
                  <span style={{ color: "rgba(255,255,255,0.55)" }}>·</span>
                  <span>{flat.subproject?.subproject_name} · {flat.flat?.flat_number}</span>
                </div>
              </div>

              <div className="flex-1 min-h-4"/>

              {/* Stats panel with solid dark scrim */}
              <div className="rounded-2xl p-3 sm:p-4 flex items-end justify-between gap-3 flex-wrap" data-no-shadow style={{ background: "rgba(0,0,0,0.78)", backdropFilter: "blur(10px)", WebkitBackdropFilter: "blur(10px)", boxShadow: "0 6px 20px rgba(0,0,0,0.35)" }}>
                {possessionDays != null && (
                  <div>
                    <div className="uppercase tracking-wider" style={{ color: "#fff", opacity: 0.9, fontSize: 10, fontWeight: 600 }}>Possession in</div>
                    <div className="flex items-baseline gap-2 mt-1">
                      <div className="rc-countdown rc-pulse rc-tabular" data-testid="possession-days" style={{ fontSize: "clamp(38px, 11vw, 60px)", color: "#F9A61C", lineHeight: 1 }}>{Math.max(0, possessionDays)}</div>
                      <div className="text-white text-sm">days</div>
                    </div>
                  </div>
                )}
                <div className="text-right min-w-[100px]">
                  <div className="uppercase tracking-wider" style={{ color: "#fff", opacity: 0.9, fontSize: 10, fontWeight: 600 }}>Construction</div>
                  <div className="rc-tabular rc-gradient-text" style={{ fontSize: "clamp(20px, 6vw, 30px)", fontWeight: 700 }}>{completionPct}%</div>
                </div>
              </div>
            </div>
          </>
        ) : <Skeleton h={240}/>}
      </div>

      {/* Outstanding banner */}
      {outstanding > 0 && (
        <Link to="/buyer/financials" className="block rc-card rc-card-padded rc-rise" style={{ background: "linear-gradient(135deg, #FFF7EC 0%, #FFEED4 100%)", borderLeft: "3px solid var(--rc-orange)" }} data-testid="outstanding-banner">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="rc-icon-tile rc-icon-tile-orange"><AlertCircle/></div>
              <div>
                <div className="rc-headline sm:hidden">{fmtINRcompact(outstanding)} outstanding</div>
                <div className="rc-headline hidden sm:block">{fmtINR(outstanding)} outstanding</div>
                <div className="rc-footnote">Tap to view your payment schedule</div>
              </div>
            </div>
            <ChevronRight className="text-[var(--rc-orange)]"/>
          </div>
        </Link>
      )}

      {/* RM card */}
      {flat?.rm && (
        <div className="rc-card rc-card-padded rc-rise" style={{ animationDelay: "60ms" }} data-testid="rm-card">
          <div className="flex items-center gap-3">
            <div className="relative flex-shrink-0">
              <div className="w-12 h-12 rounded-full flex items-center justify-center text-white text-base font-semibold" style={{ background: "linear-gradient(135deg, #0F4B7F 0%, #1976D2 100%)" }}>
                {(flat.rm.employee_name || "RM").split(" ").map(s => s[0]).slice(0,2).join("")}
              </div>
              <span className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 bg-[var(--rc-success)] rounded-full border-2 border-white"/>
            </div>
            <div className="flex-1 min-w-0">
              <div className="rc-caption uppercase tracking-wider" style={{ color: "var(--rc-blue)", fontSize: 10 }}>Relationship Manager</div>
              <div className="rc-headline truncate">{flat.rm.employee_name}</div>
              <div className="rc-caption truncate">{flat.rm.phone} · 10am – 7pm</div>
            </div>
          </div>
          <div className="mt-3 grid grid-cols-2 gap-2">
            <a href={`tel:${flat.rm.phone}`} className="rc-btn rc-btn-sm rc-btn-secondary justify-center"><Phone size={14}/>Call</a>
            <Link to="/buyer/tickets/new" className="rc-btn rc-btn-sm rc-btn-primary justify-center"><MessageCircle size={14}/>Message</Link>
          </div>
        </div>
      )}

      {/* Quick action grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { to: "/buyer/financials", Icon: Wallet,             label: "Financials", sub: "Schedule & SOA",       cls: "rc-icon-tile-blue",   tid: "qa-financials" },
          { to: "/buyer/progress",   Icon: Hammer,              label: "Progress",   sub: `${completionPct}% complete`, cls: "rc-icon-tile-orange", tid: "qa-progress" },
          { to: "/buyer/documents",  Icon: FileText,            label: "Documents",  sub: "Agreement & more",      cls: "rc-icon-tile-gold",   tid: "qa-documents" },
          { to: "/buyer/tickets",    Icon: MessageSquareWarning,label: "Queries",    sub: "Raise & track",         cls: "rc-icon-tile-blue",   tid: "qa-tickets" },
        ].map(({ to, Icon, label, sub, cls, tid }, i) => (
          <Link key={to} to={to} className="rc-card rc-rise group" style={{ padding: 14, animationDelay: `${100 + i*60}ms` }} data-testid={tid}>
            <div className={`rc-icon-tile ${cls} mb-2 transition-transform group-hover:scale-105`} style={{ width: 40, height: 40, borderRadius: 12 }}>
              <Icon size={18} strokeWidth={1.75}/>
            </div>
            <div className="rc-headline" style={{ fontSize: 15 }}>{label}</div>
            <div className="rc-caption truncate">{sub}</div>
          </Link>
        ))}
      </div>

      {/* Activity */}
      <div className="rc-card rc-card-padded rc-rise" style={{ animationDelay: "240ms" }}>
        <div className="flex items-center justify-between mb-1">
          <div>
            <div className="rc-eyebrow">Recent activity</div>
            <div className="rc-title-3 mt-1">What&apos;s happening with your home</div>
          </div>
        </div>
        <div className="divide-y divide-[var(--rc-line)] mt-3">
          {!activity && Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} h={48} className="my-3" />)}
          {activity?.length === 0 && (
            <div className="rc-footnote py-6 text-center">All quiet — we&apos;ll notify you on any updates.</div>
          )}
          {activity?.slice(0, 6).map((a, i) => {
            const Icon = a.type === "payment" ? Wallet : a.type === "milestone" ? Hammer : MessageCircle;
            const tone = a.type === "payment" ? "rc-icon-tile-green" : a.type === "milestone" ? "rc-icon-tile-orange" : "rc-icon-tile-blue";
            return (
              <div key={i} className="py-3 flex items-start gap-3" data-testid={`activity-${i}`}>
                <div className={`rc-icon-tile ${tone} flex-shrink-0`} style={{ width: 40, height: 40, borderRadius: 12 }}>
                  <Icon size={18} strokeWidth={1.75}/>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="rc-body" style={{ display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden", lineHeight: 1.3 }}>{a.text}</div>
                  <div className="rc-caption mt-0.5">{relativeTime(a.time)}</div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Handover preview */}
      <Link to="/buyer/handover" className="block rc-card rc-card-padded rc-rise group" style={{ animationDelay: "300ms" }}>
        <div className="flex items-center gap-4">
          <div className="rc-icon-tile rc-icon-tile-gold"><KeyRound/></div>
          <div className="flex-1">
            <div className="rc-headline">Handover checklist</div>
            <div className="rc-footnote">Civil · Electrical · Plumbing · Fittings · Documentation</div>
          </div>
          <ChevronRight className="text-[var(--rc-label-secondary)] transition-transform group-hover:translate-x-1"/>
        </div>
      </Link>
    </div>
  );
}
